import type { AnyRouter } from '@tanstack/react-router'
import {
  type Analytics,
  AnalyticsContext,
  type AnalyticsDeployMode,
  noopAnalytics,
  useAnalytics,
} from '@zealth/core/analytics'
import { isInIframe } from '@zealth/core/env/isInIframe'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import type { ReactNode } from 'react'

import { createPostHogAnalytics } from './createPostHogAnalytics'

export type { Analytics } from '@zealth/core/analytics'
export { useAnalytics }

// Module-level analytics client. Starts as no-op; initAnalytics replaces
// it with the real PostHog adapter when VITE_POSTHOG_KEY is set.
let analyticsRef: Analytics = noopAnalytics

export function initAnalytics(): void {
  // Suppress analytics entirely inside the playground iframe — those sessions
  // would otherwise pollute production metrics. Leaving analyticsRef as the
  // no-op means posthog.init never runs and every track/pageview is a no-op.
  if (isInIframe()) return

  analyticsRef =
    createPostHogAnalytics({
      apiKey: import.meta.env.VITE_POSTHOG_KEY,
      apiHost: import.meta.env.VITE_POSTHOG_HOST,
      deployMode: import.meta.env.VITE_POSTHOG_DEPLOY_MODE as
        AnalyticsDeployMode | undefined,
      client: 'webapp',
    }) ?? noopAnalytics
}

// Shows or hides PostHog's support chat (conversations) widget. Returns
// false when the conversations module isn't ready yet — not loaded, or not
// enabled in remote config — so callers can retry once it becomes available.
// No-op (returns true) when PostHog isn't configured (no key / init failed).
export function applySupportWidgetVisibility(visible: boolean): boolean {
  if (analyticsRef === noopAnalytics) return true

  const { conversations } = posthog
  if (!conversations.isAvailable()) return false

  if (visible) conversations.show()
  else conversations.hide()
  return true
}

// Subscribes to TanStack Router's onResolved to emit $pageview with
// route_id (the route pattern, e.g. /_app/surveys/$surveyId/submissions)
// instead of the raw URL. PostHog's built-in capture_pageview is disabled
// so only these manually-fired events reach the PostHog stream.
// Returns the router unsubscribe function.
export function registerRouterPageviews(router: AnyRouter): () => void {
  return router.subscribe('onResolved', () => {
    const matches = router.state.matches
    const leafMatch = matches[matches.length - 1]
    if (!leafMatch || !leafMatch.routeId.startsWith('/')) return
    // $current_url is overridden with the route pattern so PostHog never
    // stores real entity IDs (e.g. /surveys/abc123) in the URL field.
    analyticsRef.track('$pageview', {
      route_id: leafMatch.routeId,
      $current_url: window.location.origin + leafMatch.routeId,
    })
  })
}

// Wraps children with <PostHogProvider> only when PostHog is configured
// (so usePostHog() works for feature flags etc.). Either way, exposes
// the Analytics value via context.
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const inner = (
    <AnalyticsContext.Provider value={analyticsRef}>
      {children}
    </AnalyticsContext.Provider>
  )
  if (analyticsRef === noopAnalytics) return inner
  return <PostHogProvider client={posthog}>{inner}</PostHogProvider>
}
